var __accessCheck = (obj, member, msg) => {
  if (!member.has(obj))
    throw TypeError("Cannot " + msg);
};
var __privateGet = (obj, member, getter) => {
  __accessCheck(obj, member, "read from private field");
  return getter ? getter.call(obj) : member.get(obj);
};
var __privateAdd = (obj, member, value) => {
  if (member.has(obj))
    throw TypeError("Cannot add the same private member more than once");
  member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
};
var __privateSet = (obj, member, value, setter) => {
  __accessCheck(obj, member, "write to private field");
  setter ? setter.call(obj, value) : member.set(obj, value);
  return value;
};
var _abortController, _customValidators, _host, _showErrors, _target, _nativeTarget, _targetFn, _nativeTargetFn, _validationCompleteResolver, _validationPending, _validators, _errorMessageId, _slotName;
import { html } from "lit";
import { I18nController } from "../i18n.js";
const isNative = (target) => target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement;
let createUniqueId = 0;
class ValidationController {
  constructor(host, { target, validators = [], nativeTarget }) {
    /** An internal abort controller for cancelling pending async validation. */
    __privateAdd(this, _abortController, void 0);
    /** Custom validators set at runtime. */
    __privateAdd(this, _customValidators, void 0);
    /** Controller host. */
    __privateAdd(this, _host, void 0);
    __privateAdd(this, _showErrors, void 0);
    /**
     * The element which is being validated. Either a Form Associated
     * Custom Element, or an `<input>` or `<textarea>`.
     */
    __privateAdd(this, _target, void 0);
    /**
     * The element which is being validated. Either a Form Associated
     * Custom Element, or an `<input>` or `<textarea>`.
     */
    __privateAdd(this, _nativeTarget, void 0);
    /** The target is set after the host's connectedCallback has run. */
    __privateAdd(this, _targetFn, void 0);
    /** The target is set after the host's connectedCallback has run. */
    __privateAdd(this, _nativeTargetFn, void 0);
    /** Save a reference to the validation complete resolver */
    __privateAdd(this, _validationCompleteResolver, void 0);
    __privateAdd(this, _validationPending, void 0);
    __privateAdd(this, _validators, void 0);
    __privateAdd(this, _errorMessageId, void 0);
    /** Name of slotted validation message. */
    __privateAdd(this, _slotName, void 0);
    __privateSet(this, _showErrors, false);
    this.validationComplete = Promise.resolve();
    __privateSet(this, _validationPending, false);
    __privateSet(this, _validators, []);
    __privateSet(this, _errorMessageId, `validation-message-error-${createUniqueId++}`);
    this.onInvalid = (event) => {
      event.stopImmediatePropagation();
      event.preventDefault();
      this.setInvalidState();
      if (__privateGet(this, _showErrors) !== !this.validity.valid) {
        const state = this.getInvalidState(this.validity);
        if (!state) {
          return;
        }
        __privateSet(this, _slotName, state.replace(/_/g, "-"));
        if (this.nativeTarget) {
          if (this.nativeTarget.hasAttribute("aria-describedby")) {
            const currentId = this.nativeTarget.getAttribute("aria-describedby");
            if (!currentId.includes("error")) {
              this.nativeTarget.setAttribute("aria-describedby", `${currentId} ${__privateGet(this, _errorMessageId)}`);
            }
          } else {
            this.nativeTarget.setAttribute("aria-describedby", __privateGet(this, _errorMessageId));
          }
        }
        this.updateValidationMessage();
        __privateSet(this, _showErrors, !this.validity.valid);
        __privateGet(this, _host).requestUpdate();
      }
    };
    this.onReset = (event) => {
      const { form } = isNative(this.target) ? this.target : this.target.internals;
      if (form === event.target) {
        __privateSet(this, _showErrors, false);
        this.removeInvalidState();
      }
    };
    __privateSet(this, _host, host);
    __privateGet(this, _host).addController(this);
    this.i18nController = new I18nController(host);
    if (typeof target === "function") {
      __privateSet(this, _targetFn, target);
    } else {
      __privateSet(this, _target, target || host);
    }
    if (nativeTarget && typeof nativeTarget === "function") {
      __privateSet(this, _nativeTargetFn, nativeTarget);
    }
    __privateSet(this, _validators, validators || []);
    __privateSet(this, _slotName, "");
  }
  get target() {
    if (__privateGet(this, _target)) {
      return __privateGet(this, _target);
    } else {
      throw new Error("The validation controller cannot function without a target");
    }
  }
  get nativeTarget() {
    if (__privateGet(this, _nativeTarget)) {
      return __privateGet(this, _nativeTarget);
    } else {
      throw new Error("The validation controller cannot function without a native form element");
    }
  }
  get validationMessage() {
    if (isNative(this.target)) {
      return this.target.validationMessage;
    } else {
      return this.target.internals.validationMessage;
    }
  }
  get validity() {
    if (isNative(this.target)) {
      return this.target.validity;
    } else {
      return this.target.internals.validity;
    }
  }
  async hostConnected() {
    await __privateGet(this, _host).updateComplete;
    if (!__privateGet(this, _target) && __privateGet(this, _targetFn)) {
      __privateSet(this, _target, __privateGet(this, _targetFn).call(this));
    }
    if (!__privateGet(this, _nativeTarget) && __privateGet(this, _nativeTargetFn)) {
      __privateSet(this, _nativeTarget, __privateGet(this, _nativeTargetFn).call(this));
    }
    if (isNative(this.target) && !this.target.hasAttribute("title")) {
      this.target.setAttribute("title", "");
    }
    if (isNative(this.nativeTarget) && !this.nativeTarget.hasAttribute("title")) {
      this.nativeTarget.setAttribute("title", "");
    }
    document.addEventListener("reset", this.onReset);
    if (isNative(this.target)) {
      this.target.addEventListener("invalid", this.onInvalid);
    } else {
      __privateGet(this, _host).addEventListener("invalid", this.onInvalid);
      this.nativeTarget.addEventListener("invalid", this.onInvalid);
    }
  }
  hostDisconnected() {
    if (isNative(this.target)) {
      this.target.removeEventListener("invalid", this.onInvalid);
    } else {
      this.nativeTarget.removeEventListener("invalid", this.onInvalid);
    }
    document.removeEventListener("reset", this.onReset);
  }
  hostUpdated() {
    if ("validators" in __privateGet(this, _host)) {
      __privateSet(this, _customValidators, __privateGet(this, _host).validators);
    }
  }
  render() {
    if (!__privateGet(this, _target) || !this.target || !__privateGet(this, _host)) {
      return;
    }
    const state = this.getInvalidState(this.validity);
    if (__privateGet(this, _showErrors) && !!state) {
      __privateSet(this, _slotName, state.replace(/_/g, "-"));
      this.setInvalidState();
      this.updateValidationMessage();
      return html`<slot .name=${__privateGet(this, _slotName)} part="error" @slotchange=${this.onSlotchange}></slot>`;
    } else {
      this.removeInvalidState();
    }
  }
  onSlotchange(event) {
    const elements = event.target.assignedElements({ flatten: true });
    if (elements.length > 1) {
      const nonCustomErrors = elements.filter((element) => element.tagName === "LEAF-VALIDATION-MESSAGE");
      nonCustomErrors.forEach((error) => error.remove());
    }
  }
  setInvalidState() {
    if (!__privateGet(this, _target) || !this.target || !__privateGet(this, _host)) {
      return;
    }
    __privateGet(this, _target).setAttribute("invalid", "");
    if (isNative(this.target)) {
      __privateGet(this, _host).setAttribute("invalid", "");
      this.target.ariaInvalid = "true";
      __privateGet(this, _host).requestUpdate();
    }
  }
  removeInvalidState() {
    this.removeValidationMessage();
    __privateGet(this, _host).removeAttribute("invalid");
    this.target.ariaInvalid = null;
    __privateGet(this, _host).requestUpdate();
  }
  updateValidationMessage() {
    const errorPart = __privateGet(this, _host).querySelector('[part="error"]'), currentError = __privateGet(this, _host).querySelector("leaf-validation-message");
    if (!currentError && this.validationMessage || (currentError == null ? void 0 : currentError.slot) !== __privateGet(this, _slotName) || this.validationMessage !== (errorPart == null ? void 0 : errorPart.innerText)) {
      if (currentError) {
        currentError.remove();
      }
      if (this.nativeTarget) {
        if (this.nativeTarget.hasAttribute("aria-describedby")) {
          const currentId = this.nativeTarget.getAttribute("aria-describedby");
          if (!currentId.includes("error")) {
            this.nativeTarget.setAttribute("aria-describedby", `${currentId} ${__privateGet(this, _errorMessageId)}`);
          }
        } else {
          this.nativeTarget.setAttribute("aria-describedby", __privateGet(this, _errorMessageId));
        }
      }
      const formLabel = `<leaf-validation-message variant="error" slot="${__privateGet(this, _slotName)}" part="error">
      <span id=${__privateGet(this, _errorMessageId)} aria-live="polite" role="alert">${this.validationMessage}</span>
      </leaf-validation-message>`;
      __privateGet(this, _host).insertAdjacentHTML("beforeend", formLabel);
    }
  }
  removeValidationMessage() {
    var _a;
    (_a = __privateGet(this, _host).querySelector('[part="error"]')) == null ? void 0 : _a.remove();
    try {
      if (this.nativeTarget && this.nativeTarget.hasAttribute("aria-describedby")) {
        const describedBy = this.nativeTarget.getAttribute("aria-describedby"), ids = describedBy == null ? void 0 : describedBy.split(" "), index = ids == null ? void 0 : ids.indexOf(__privateGet(this, _errorMessageId));
        if (index !== -1 && ids && this.nativeTarget && (ids == null ? void 0 : ids.length) > 1) {
          if (index) {
            ids.splice(index, 1);
          }
          const newAriaDescribedby = ids == null ? void 0 : ids.join(" ");
          if (newAriaDescribedby) {
            this.nativeTarget.setAttribute("aria-describedby", newAriaDescribedby.toString());
          }
        }
      }
    } catch (error) {
      return;
    }
  }
  setCustomValidity(message) {
    if (isNative(this.target)) {
      this.target.setCustomValidity(message);
    } else {
      this.target.internals.setValidity({ customError: true }, message);
    }
  }
  validate(value) {
    const validators = [...__privateGet(this, _validators), ...__privateGet(this, _customValidators) || []], hasAsyncValidators = validators.some(({ isValid }) => isValid instanceof Promise), asyncValidators = [], validity = {};
    if (!__privateGet(this, _validationPending)) {
      this.validationComplete = new Promise((resolve) => {
        __privateSet(this, _validationCompleteResolver, resolve);
      });
      __privateSet(this, _validationPending, true);
    }
    if (__privateGet(this, _abortController)) {
      __privateGet(this, _abortController).abort();
      this.previousAbortController = __privateGet(this, _abortController);
    }
    const abortController = __privateSet(this, _abortController, new AbortController());
    if (!validators.length) {
      return;
    }
    let keyChanged = false, validationMessage = void 0;
    validators.forEach((validator) => {
      const key = validator.key || "customError", isValid = validator.isValid(this.target, value, abortController.signal), isAsyncValidator = isValid instanceof Promise;
      if (isAsyncValidator) {
        asyncValidators.push(isValid);
        isValid.then((valid) => {
          if (valid === void 0 || valid === null) {
            return;
          }
          validity[key] = !valid;
          const currentVal = value && typeof value === "string" ? String(value.length) : "0";
          if (validator.key === "rangeOverflow" && "maxLength" in __privateGet(this, _host) && __privateGet(this, _host).hasAttribute("max-length")) {
            validationMessage = this.i18nController.term(validator.key, String(__privateGet(this, _host).maxLength), currentVal);
          } else if (validator.key === "rangeUnderflow" && "minLength" in __privateGet(this, _host) && __privateGet(this, _host).hasAttribute("min-length")) {
            validationMessage = this.i18nController.term(validator.key, String(__privateGet(this, _host).minLength), currentVal);
          } else if (validator.key === "customError" && "error" in __privateGet(this, _host)) {
            validationMessage = String(__privateGet(this, _host).error);
          } else if (validator.key === "rangeOverflow" && __privateGet(this, _host).hasAttribute("max-date") || validator.key === "rangeUnderflow" && __privateGet(this, _host).hasAttribute("min-date")) {
            validationMessage = this.i18nController.term("dateRange");
          } else {
            validationMessage = this.i18nController.term(validator.key);
          }
          this.setValidityWithOptionalTarget(validity, validationMessage);
        });
      } else {
        validity[key] = !isValid;
        if (this.validity[key] !== !isValid) {
          keyChanged = true;
        }
        if (!isValid) {
          const currentVal = value && typeof value === "string" ? String(value.length) : "0";
          if (validator.key === "rangeOverflow" && "maxLength" in __privateGet(this, _host) && __privateGet(this, _host).hasAttribute("max-length")) {
            validationMessage = this.i18nController.term(validator.key, String(__privateGet(this, _host).maxLength), currentVal);
          } else if (validator.key === "rangeUnderflow" && "minLength" in __privateGet(this, _host) && __privateGet(this, _host).hasAttribute("min-length")) {
            validationMessage = this.i18nController.term(validator.key, String(__privateGet(this, _host).minLength), currentVal);
          } else if (validator.key === "customError" && "error" in __privateGet(this, _host)) {
            validationMessage = String(__privateGet(this, _host).error);
          } else if (validator.key === "rangeOverflow" && __privateGet(this, _host).hasAttribute("max-date") || validator.key === "rangeUnderflow" && __privateGet(this, _host).hasAttribute("min-date")) {
            validationMessage = this.i18nController.term("dateRange");
          } else {
            validationMessage = this.i18nController.term(validator.key);
          }
        }
      }
    });
    Promise.allSettled(asyncValidators).then(() => {
      var _a;
      if (!(abortController == null ? void 0 : abortController.signal.aborted)) {
        __privateSet(this, _validationPending, false);
        (_a = __privateGet(this, _validationCompleteResolver)) == null ? void 0 : _a.call(this);
      }
    });
    if (keyChanged || !hasAsyncValidators) {
      this.setValidityWithOptionalTarget(validity, validationMessage);
    }
  }
  getInvalidState(validity) {
    if (validity.badInput) {
      return "badInput";
    } else if (validity.patternMismatch) {
      return "patternMismatch";
    } else if (validity.rangeOverflow) {
      return "rangeOverflow";
    } else if (validity.rangeUnderflow) {
      return "rangeUnderflow";
    } else if (validity.stepMismatch) {
      return "stepMismatch";
    } else if (validity.tooLong) {
      return "tooLong";
    } else if (validity.tooShort) {
      return "tooShort";
    } else if (validity.typeMismatch) {
      return "typeMismatch";
    } else if (validity.valueMissing) {
      return "valueMissing";
    } else if (validity.customError) {
      return "customError";
    }
  }
  setValidityWithOptionalTarget(validity, validationMessage) {
    if (isNative(this.target)) {
      this.target.setCustomValidity(validationMessage ?? "");
    } else {
      this.target.internals.setValidity(validity, validationMessage);
    }
    __privateGet(this, _host).requestUpdate();
  }
}
_abortController = new WeakMap();
_customValidators = new WeakMap();
_host = new WeakMap();
_showErrors = new WeakMap();
_target = new WeakMap();
_nativeTarget = new WeakMap();
_targetFn = new WeakMap();
_nativeTargetFn = new WeakMap();
_validationCompleteResolver = new WeakMap();
_validationPending = new WeakMap();
_validators = new WeakMap();
_errorMessageId = new WeakMap();
_slotName = new WeakMap();
export {
  ValidationController
};
//# sourceMappingURL=validation.js.map
